import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Modal, Button, Spin, Empty, notification, Tabs, Tag } from 'antd';
import {
    PlayCircleOutlined,
    PauseCircleOutlined,
    SoundOutlined,
    FileTextOutlined,
    DownOutlined,
    RightOutlined,
    LoadingOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../../enums/api.enums';

interface AudioDetailModalProps {
    open: boolean;
    taskId: string;
    voiceName: string;
    onClose: () => void;
}

interface Chapter {
    chapter: number;
    title: string;
}

interface TranscriptData {
    chapter: number;
    title: string;
    text: string;
    sentences: string[];
}

interface SummaryTranscriptData {
    chapter: number;
    title: string;
    text: string;
    sentences: string[];
}

interface SentenceTimestamp {
    text: string;
    start: number;
    end: number;
}

const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const buildTimestamps = (sentences: string[], totalDuration: number): SentenceTimestamp[] => {
    if (!sentences || sentences.length === 0) return [];
    const totalChars = sentences.reduce((sum, s) => sum + s.length, 0);
    let currentTime = 0;
    return sentences.map((text) => {
        const charWeight = text.length / totalChars;
        const duration = charWeight * totalDuration;
        const start = currentTime;
        const end = currentTime + duration;
        currentTime = end;
        return { text, start, end };
    });
};

const AudioDetailModal: React.FC<AudioDetailModalProps> = ({ open, taskId, voiceName, onClose }) => {
    const [chapters, setChapters] = useState<Chapter[]>([]);
    const [loading, setLoading] = useState(false);
    const [playingChapter, setPlayingChapter] = useState<number | null>(null);
    const [playingType, setPlayingType] = useState<'audio' | 'summary' | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [audioDuration, setAudioDuration] = useState(0);

    const [expandedChapter, setExpandedChapter] = useState<number | null>(null);
    const [transcriptTab, setTranscriptTab] = useState<'full' | 'summary'>('full');
    const [transcriptLoading, setTranscriptLoading] = useState(false);
    const [transcriptData, setTranscriptData] = useState<TranscriptData | null>(null);
    const [summaryData, setSummaryData] = useState<SummaryTranscriptData | null>(null);
    const [chapterDurations, setChapterDurations] = useState<Record<number, number>>({});

    const transcriptContainerRef = useRef<HTMLDivElement>(null);
    const sentenceRefs = useRef<Map<number, HTMLDivElement>>(new Map);

    useEffect(() => {
        if (!open || !taskId) return;
        setLoading(true);
        setChapters([]);
        setExpandedChapter(null);
        setTranscriptData(null);
        setSummaryData(null);
        setCurrentTime(0);
        setAudioDuration(0);
        axios.get(`${API_URL.HOST_UPLOAD_VOICE}/task/${taskId}/chapters`)
            .then((res) => {
                setChapters(res.data?.chapters || []);
            })
            .catch(() => {
                notification.error({ message: "Không thể tải danh sách chương" });
            })
            .finally(() => setLoading(false));
    }, [open, taskId]);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    const fetchTranscript = useCallback(async (chapter: number) => {
        setTranscriptLoading(true);
        setTranscriptData(null);
        setSummaryData(null);
        try {
            const [transcriptRes, summaryRes] = await Promise.allSettled([
                axios.get(`${API_URL.HOST_UPLOAD_VOICE}/task/${taskId}/chapter/${chapter}/transcript`),
                axios.get(`${API_URL.HOST_UPLOAD_VOICE}/task/${taskId}/chapter/${chapter}/summary-transcript`),
            ]);
            if (transcriptRes.status === 'fulfilled') {
                setTranscriptData(transcriptRes.value.data);
            }
            if (summaryRes.status === 'fulfilled') {
                setSummaryData(summaryRes.value.data);
            }
        } catch {
            // silent
        } finally {
            setTranscriptLoading(false);
        }
    }, [taskId]);

    const handleToggleExpand = (chapter: number) => {
        if (expandedChapter === chapter) {
            setExpandedChapter(null);
            setTranscriptData(null);
            setSummaryData(null);
        } else {
            setExpandedChapter(chapter);
            setTranscriptTab('full');
            fetchTranscript(chapter);
            preloadAudioDuration(chapter);
        }
    };

    const preloadAudioDuration = (chapter: number) => {
        if (chapterDurations[chapter]) return;
        const url = `${API_URL.HOST_UPLOAD_VOICE}/static/${taskId}/audios/${chapter}/chapter_${chapter}.mp3`;
        const audio = new Audio();
        audio.addEventListener('loadedmetadata', () => {
            setChapterDurations((prev) => ({ ...prev, [chapter]: audio.duration || 0 }));
        });
        audio.addEventListener('error', () => {
            setChapterDurations((prev) => ({ ...prev, [chapter]: 0 }));
        });
        audio.src = url;
    };

    const handlePlay = (chapter: number, type: 'audio' | 'summary') => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }

        if (playingChapter === chapter && playingType === type) {
            setPlayingChapter(null);
            setPlayingType(null);
            setCurrentTime(0);
            setAudioDuration(0);
            return;
        }

        setCurrentTime(0);
        setAudioDuration(0);

        const suffix = type === 'summary' ? '_summary' : '';
        const url = `${API_URL.HOST_UPLOAD_VOICE}/static/${taskId}/audios/${chapter}/chapter_${chapter}${suffix}.mp3`;

        const audio = new Audio(url);
        audioRef.current = audio;

        audio.addEventListener('loadedmetadata', () => {
            setAudioDuration(audio.duration || 0);
        });

        audio.addEventListener('timeupdate', () => {
            setCurrentTime(audio.currentTime || 0);
        });

        audio.onended = () => {
            setPlayingChapter(null);
            setPlayingType(null);
            setCurrentTime(0);
        };
        audio.onerror = () => {
            notification.warning({
                message: "Không thể phát audio",
                description: type === 'summary' ? `Chương ${chapter} chưa có bản tóm tắt` : `Chương ${chapter} chưa có audio`,
            });
            setPlayingChapter(null);
            setPlayingType(null);
            setCurrentTime(0);
            setAudioDuration(0);
        };
        audio.play().catch(() => {
            setPlayingChapter(null);
            setPlayingType(null);
        });

        setPlayingChapter(chapter);
        setPlayingType(type);
    };

    const getCurrentSentenceIndex = (timestamps: SentenceTimestamp[]): number => {
        for (let i = 0; i < timestamps.length; i++) {
            if (currentTime >= timestamps[i].start && currentTime < timestamps[i].end) {
                return i;
            }
        }
        if (timestamps.length > 0 && currentTime >= timestamps[timestamps.length - 1].end) {
            return timestamps.length - 1;
        }
        return -1;
    };

    const scrollToSentence = (index: number) => {
        const el = sentenceRefs.current.get(index);
        if (el && transcriptContainerRef.current) {
            const container = transcriptContainerRef.current;
            const elTop = el.offsetTop;
            const elHeight = el.offsetHeight;
            const containerTop = container.scrollTop;
            const containerHeight = container.clientHeight;
            if (elTop < containerTop || elTop + elHeight > containerTop + containerHeight) {
                container.scrollTo({
                    top: elTop - containerHeight / 3,
                    behavior: 'smooth',
                });
            }
        }
    };

    useEffect(() => {
        if (playingChapter && expandedChapter === playingChapter) {
            const data = transcriptTab === 'full' ? transcriptData : summaryData;
            if (data?.sentences && data.sentences.length > 0 && audioDuration > 0) {
                const timestamps = buildTimestamps(data.sentences, audioDuration);
                const idx = getCurrentSentenceIndex(timestamps);
                if (idx >= 0) {
                    scrollToSentence(idx);
                }
            }
        }
    }, [currentTime, playingChapter, expandedChapter, transcriptTab, transcriptData, summaryData, audioDuration]);

    const renderTranscriptContent = () => {
        if (transcriptLoading) {
            return (
                <div style={{ textAlign: 'center', padding: 20 }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} tip="Đang tải transcript..." />
                </div>
            );
        }

        const data = transcriptTab === 'full' ? transcriptData : summaryData;

        if (!data) {
            return (
                <Empty
                    description={transcriptTab === 'full' ? "Chưa có transcript" : "Chưa có bản tóm tắt"}
                    style={{ padding: 16 }}
                />
            );
        }

        const effectiveDuration = audioDuration > 0 ? audioDuration : (expandedChapter ? chapterDurations[expandedChapter] || 0 : 0);
        const isCurrentlyPlaying = playingChapter === expandedChapter && effectiveDuration > 0;
        const sentences = data.sentences && data.sentences.length > 0 ? data.sentences : null;
        const timestamps = effectiveDuration > 0 && sentences ? buildTimestamps(sentences, effectiveDuration) : [];
        const activeIdx = isCurrentlyPlaying ? getCurrentSentenceIndex(timestamps) : -1;

        return (
            <div
                ref={transcriptContainerRef}
                style={{ padding: '8px 0', maxHeight: 300, overflow: 'auto' }}
            >
                {data.title && (
                    <div style={{ fontWeight: 500, marginBottom: 12, color: '#333', fontSize: 14 }}>
                        {data.title}
                    </div>
                )}
                {sentences ? (
                    <div>
                        {sentences.map((sentence, idx) => {
                            if (timestamps.length > 0) {
                                const ts = timestamps[idx];
                                const isActive = isCurrentlyPlaying && idx === activeIdx;
                                return (
                                    <div
                                        key={idx}
                                        ref={(el) => {
                                            if (el) sentenceRefs.current.set(idx, el);
                                        }}
                                        style={{
                                            display: 'flex',
                                            gap: 10,
                                            padding: '8px 10px',
                                            borderRadius: 6,
                                            marginBottom: 4,
                                            background: isActive ? '#e6f7ff' : 'transparent',
                                            borderLeft: isActive ? '3px solid #1890ff' : '3px solid transparent',
                                            transition: 'all 0.2s ease',
                                            cursor: isCurrentlyPlaying ? 'pointer' : 'default',
                                        }}
                                        onClick={() => {
                                            if (isCurrentlyPlaying && audioRef.current) {
                                                audioRef.current.currentTime = ts.start;
                                            }
                                        }}
                                    >
                                        <div style={{
                                            minWidth: 100,
                                            fontSize: 12,
                                            color: isActive ? '#1890ff' : '#999',
                                            fontFamily: 'monospace',
                                            fontWeight: isActive ? 600 : 400,
                                            paddingTop: 2,
                                            userSelect: 'none',
                                        }}>
                                            {formatTime(ts.start)} - {formatTime(ts.end)}
                                        </div>
                                        <div style={{
                                            flex: 1,
                                            lineHeight: 1.7,
                                            color: isActive ? '#1890ff' : '#555',
                                            fontWeight: isActive ? 500 : 400,
                                            fontSize: 13,
                                        }}>
                                            {sentence}
                                        </div>
                                    </div>
                                );
                            }
                            return (
                                <div
                                    key={idx}
                                    style={{
                                        padding: '6px 10px',
                                        marginBottom: 4,
                                        lineHeight: 1.7,
                                        color: '#555',
                                        fontSize: 13,
                                    }}
                                >
                                    {sentence}
                                </div>
                            );
                        })}
                    </div>
                ) : data.text ? (
                    <div style={{ lineHeight: 1.8, color: '#555', whiteSpace: 'pre-wrap', fontSize: 13 }}>
                        {data.text}
                    </div>
                ) : (
                    <Empty description="Không có nội dung" style={{ padding: 16 }} />
                )}
            </div>
        );
    };

    return (
        <Modal
            title={`Audio sách - ${voiceName}`}
            open={open}
            onCancel={() => {
                if (audioRef.current) {
                    audioRef.current.pause();
                    audioRef.current = null;
                }
                setPlayingChapter(null);
                setPlayingType(null);
                setExpandedChapter(null);
                setTranscriptData(null);
                setSummaryData(null);
                setCurrentTime(0);
                setAudioDuration(0);
                onClose();
            }}
            footer={null}
            width={750}
            destroyOnClose
        >
            {loading ? (
                <div style={{ textAlign: 'center', padding: 32 }}>
                    <Spin />
                </div>
            ) : chapters.length === 0 ? (
                <Empty description="Chưa có chương nào" />
            ) : (
                <div>
                    {chapters.map((ch) => {
                        const isExpanded = expandedChapter === ch.chapter;
                        const isPlaying = playingChapter === ch.chapter;
                        return (
                            <div key={ch.chapter} style={{ marginBottom: 8 }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px 12px',
                                        border: `1px solid ${isExpanded ? '#1890ff' : '#f0f0f0'}`,
                                        borderRadius: 8,
                                        background: isPlaying ? '#e6f7ff' : '#fafafa',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => handleToggleExpand(ch.chapter)}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                                        {isExpanded ? (
                                            <DownOutlined style={{ color: '#1890ff', fontSize: 12 }} />
                                        ) : (
                                            <RightOutlined style={{ color: '#999', fontSize: 12 }} />
                                        )}
                <div style={{ maxHeight: 500, overflow: 'auto' }}>
                                            <div style={{ fontWeight: 500 }}>{ch.title || `Chương ${ch.chapter}`}</div>
                                            <div style={{ fontSize: 12, color: '#999' }}>Chương {ch.chapter}</div>
                                        </div>
                                    </div>
                                    <div
                                        style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {isPlaying && audioDuration > 0 && (
                                            <span style={{ fontSize: 12, color: '#1890ff', fontFamily: 'monospace' }}>
                                                {formatTime(currentTime)} / {formatTime(audioDuration)}
                                            </span>
                                        )}
                                        <Button
                                            type={isPlaying && playingType === 'audio' ? 'primary' : 'default'}
                                            size="small"
                                            icon={isPlaying && playingType === 'audio'
                                                ? <PauseCircleOutlined />
                                                : <PlayCircleOutlined />
                                            }
                                            onClick={() => handlePlay(ch.chapter, 'audio')}
                                        >
                                            {isPlaying && playingType === 'audio' ? 'Tạm dừng' : 'Nghe'}
                                        </Button>
                                        <Button
                                            type={isPlaying && playingType === 'summary' ? 'primary' : 'default'}
                                            size="small"
                                            ghost={isPlaying && playingType === 'summary'}
                                            icon={isPlaying && playingType === 'summary'
                                                ? <PauseCircleOutlined />
                                                : <SoundOutlined />
                                            }
                                            onClick={() => handlePlay(ch.chapter, 'summary')}
                                            style={isPlaying && playingType === 'summary' ? { borderColor: '#722ed1', color: '#722ed1' } : {}}
                                        >
                                            {isPlaying && playingType === 'summary' ? 'Tạm dừng' : 'Tóm tắt'}
                                        </Button>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div style={{
                                        border: '1px solid #f0f0f0',
                                        borderTop: 'none',
                                        borderRadius: '0 0 8px 8px',
                                        padding: '8px 16px 16px',
                                        background: '#fff',
                                    }}>
                                        <Tabs
                                            activeKey={transcriptTab}
                                            onChange={(key) => setTranscriptTab(key as 'full' | 'summary')}
                                            size="small"
                                            style={{ marginBottom: 0 }}
                                            items={[
                                                {
                                                    key: 'full',
                                                    label: (
                                                        <span>
                                                            <FileTextOutlined /> Transcript gốc
                                                        </span>
                                                    ),
                                                    children: renderTranscriptContent(),
                                                },
                                                {
                                                    key: 'summary',
                                                    label: (
                                                        <span>
                                                            <SoundOutlined /> Tóm tắt
                                                            {summaryData && (
                                                                <Tag color="purple" style={{ marginLeft: 4, fontSize: 10 }}>AI</Tag>
                                                            )}
                                                        </span>
                                                    ),
                                                    children: renderTranscriptContent(),
                                                },
                                            ]}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </Modal>
    );
};

export default AudioDetailModal;
